"use client";

import {
  Order,
  HubTradeCreateOrderBody,
  OrderType,
  CAMPAIGN_PLACEHOLDER_NAME,
  FuturePosition,
  ORDER_SIDE,
  METRICS_EVENTS,
  TradeMethod,
  TradeExecutedPayload,
  MarginMode,
  FEATURE_FLAGS,
} from "@cede/types";
import { API_ENDPOINT } from "@cede/utils";
import { isPerpsExchange } from "./config";
import { CancelOrderParams } from "./types";
import { invalidateCexPerpsBalances } from "./useCexPerpsBalances";
import { invalidateCexPerpsPositions } from "./useCexPerpsPositions";
import { useApi } from "@/hooks/core/useApi";
import { useMetricsEvents } from "@/hooks/data/useMetricsEvents";
import { useFeatureFlagIsOn } from "@/hooks/feature-flags";
import { useHubUser } from "@/hooks/user/useHubUser";
import { orderV2ToOrder } from "@/utils/exchange";
import { safeDecimal } from "@/utils/hyperliquid/numeric";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Decimal from "decimal.js";
import { enqueueSnackbar } from "notistack";

interface CreateOrderParams {
  accountId: number;
  symbol: string; // CCXT format: BTC/USD:USD
  side: "buy" | "sell";
  type: OrderType;
  amount: string;
  price: string;
  leverage?: number;
  marginMode?: MarginMode;
  reduceOnly?: boolean;
  campaignId?: string;
  hteCampaignId?: string;
}

interface ClosePositionParams {
  accountId: number;
  campaignId: string;
  position: FuturePosition;
}

interface UseCexPerpsTradeOptions {
  onSuccess?: (order: Order) => void;
  onError?: (error: Error) => void;
}

/**
 * Hook for executing trades on CEX perps exchanges
 * Exchange-agnostic: pass any supported exchangeId
 */
export function useCexPerpsTrade(exchangeId: string, options: UseCexPerpsTradeOptions = {}) {
  const { onSuccess, onError } = options;
  const queryClient = useQueryClient();
  const { auth } = useHubUser();
  const apiHelper = useApi();
  const { trackEvent } = useMetricsEvents();
  const isTradeExecutedReportingEnabled = useFeatureFlagIsOn(
    FEATURE_FLAGS.TRADE_REPORTING_API_TRADE_EXECUTED_EVENT_ENABLE,
  );

  // Create order mutation
  const createOrderMutation = useMutation({
    mutationFn: async (params: CreateOrderParams): Promise<{ order: Order; params: CreateOrderParams }> => {
      if (!isPerpsExchange(exchangeId)) {
        throw new Error(`Unsupported exchange: ${exchangeId}`);
      }

      if (!auth.identityToken) {
        throw new Error("Not authenticated");
      }

      // Map params to HubTradeCreateOrderBody format
      const orderBody: HubTradeCreateOrderBody = {
        accountId: Number(params.accountId),
        pairSymbol: params.symbol,
        orderSide: params.side,
        orderType: params.type,
        amount: new Decimal(params.amount),
        price: new Decimal(params.price),
        campaignId: params.campaignId ?? CAMPAIGN_PLACEHOLDER_NAME.TRADE_PAGE,
        hteCampaignId: params.hteCampaignId,
        leverage: params.leverage,
        marginMode: params.marginMode,
        reduceOnly: params.reduceOnly,
      };

      const response = await apiHelper.postAuth(auth, API_ENDPOINT.HUB_TRADE_CREATE_ORDER, {
        bodyParams: orderBody,
        headers: {
          "sac-fetch-dest": "empty",
        },
      });

      return { order: orderV2ToOrder(response), params };
    },
    onSuccess: ({ order, params }) => {
      // Invalidate related queries
      invalidateCexPerpsPositions(queryClient, exchangeId);
      invalidateCexPerpsBalances(queryClient, exchangeId);

      // Show success notification
      const side = order.side === "buy" ? "Long" : "Short";
      const orderTypeLabel = order.type === OrderType.MARKET ? "executed" : "placed";
      enqueueSnackbar(`${side} order ${orderTypeLabel} successfully`, {
        variant: "success",
      });

      // Track TRADE_EXECUTED event with leverage and original amount
      if (isTradeExecutedReportingEnabled) {
        const effectiveLeverage = params.leverage || 1;

        // without leverage
        const initialBaseAmount = new Decimal(order.amount).div(effectiveLeverage);
        // with leverage
        const baseAmount = new Decimal(order.amount);
        // base fiat amount (with leverage)
        const baseFiatAmount = new Decimal(baseAmount).mul(order.price);
        // base fiat amount (without leverage)
        const initialBaseFiatAmount = initialBaseAmount.mul(order.price);
        const trackedMarginMode =
          params.marginMode === MarginMode.CROSS || params.marginMode === MarginMode.ISOLATED
            ? params.marginMode
            : undefined;

        const mixpanelPayload = {
          id: order.id,
          exchangeId,
          campaignName: params.campaignId ?? CAMPAIGN_PLACEHOLDER_NAME.TRADE_PAGE,
          campaignId: params.campaignId,
          pairSymbol: params.symbol,
          expectedPrice: params.price,
          amount: params.amount,
          type: order.type,
          side: order.side,
          price: order.price,
          baseAmount: order.amount,
          initialBaseAmount: initialBaseAmount.toNumber(),
          baseFiatAmount: baseFiatAmount.toNumber(),
          initialBaseFiatAmount: initialBaseFiatAmount.toNumber(),
          tradingMethod: TradeMethod.HUB,
          timestamp: new Date().toISOString(),
          chain: "offchain",
          marketType: "perps" as const,
          fromTradePage: params.campaignId === CAMPAIGN_PLACEHOLDER_NAME.TRADE_PAGE,
          accountId: params.accountId,
          leverage: params.leverage,
          marginMode: trackedMarginMode,
          reduceOnly: params.reduceOnly,
          isClosingPosition: params.reduceOnly === true,
          isOpeningPosition: params.reduceOnly !== true,
        } satisfies TradeExecutedPayload;

        trackEvent(METRICS_EVENTS.TRADE_EXECUTED, mixpanelPayload);
      }

      onSuccess?.(order);
    },
    onError: (error: Error) => {
      // Resync positions with the exchange on failure. A rejected close (e.g. Binance
      // -2022 reduceOnly against an already-flat position) otherwise leaves the phantom
      // row in place, and every re-tap fires another rejected close.
      invalidateCexPerpsPositions(queryClient, exchangeId);
      enqueueSnackbar(error.message || "Failed to create order", {
        variant: "error",
      });
      onError?.(error);
    },
  });

  // Cancel order mutation
  const cancelOrderMutation = useMutation({
    mutationFn: async (params: CancelOrderParams): Promise<void> => {
      if (!isPerpsExchange(exchangeId)) {
        throw new Error(`Unsupported exchange: ${exchangeId}`);
      }

      if (!auth.identityToken) {
        throw new Error("Not authenticated");
      }

      await apiHelper.deleteAuth(auth, API_ENDPOINT.HUB_TRADE_CANCEL_ORDER, {
        queryParams: {
          accountId: params.accountId.toString(),
          orderId: params.orderId,
          pairSymbol: params.symbol,
        },
      });
    },
    onSuccess: () => {
      // Invalidate related queries
      invalidateCexPerpsBalances(queryClient, exchangeId);

      enqueueSnackbar("Order cancelled successfully", {
        variant: "success",
      });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || "Failed to cancel order", {
        variant: "error",
      });
    },
  });

  const closePositionMutation = useMutation({
    mutationFn: async (params: ClosePositionParams): Promise<void> => {
      const { accountId, campaignId, position } = params;
      // Estimated execution (mark) price from entry price + PnL, using Decimal math
      // to avoid float precision loss on prices and small-contract PnL divisions:
      //   markPrice = entryPrice + (unrealizedPnl / |size|) * (long ? 1 : -1)
      const entryPrice = safeDecimal(position.entryPrice, "0");
      const sizeAbs = safeDecimal(position.size, "0").abs();
      const pnl = safeDecimal(position.unrealizedPnl, "0");
      const directionSign = position.positionSide === "long" ? 1 : -1;
      const markPrice = sizeAbs.gt(0) ? entryPrice.plus(pnl.div(sizeAbs).mul(directionSign)) : entryPrice;

      await createOrderMutation.mutateAsync({
        accountId,
        symbol: `${position.pairSymbol}:${position.marginTokenSymbol}`,
        side: position.positionSide === "long" ? ORDER_SIDE.SELL : ORDER_SIDE.BUY,
        type: OrderType.MARKET,
        amount: sizeAbs.toString(),
        price: markPrice.toString(),
        reduceOnly: true,
        // Pass campaignId and leverage for proper volume tracking
        campaignId: campaignId ?? CAMPAIGN_PLACEHOLDER_NAME.TRADE_PAGE,
        leverage: position.leverage ? Number(position.leverage) : undefined,
        marginMode: position.marginMode,
      });

      // Track position closure
      trackEvent(METRICS_EVENTS.CEX_PERPS_POSITION_CLOSED, {
        accountId,
        pairSymbol: position.pairSymbol,
        side: position.positionSide,
        contracts: sizeAbs.toNumber(),
        entryPrice: position.entryPrice,
        markPrice: markPrice.toNumber(),
        leverage: position.leverage,
        unrealizedPnl: position.unrealizedPnl,
        tradingMethod: "CEX_PERPS",
        timestamp: new Date().toISOString(),
      });
    },
  });

  // Wrap createOrder to return just the order (callers expect Order, not { order, params })
  const createOrder = async (params: CreateOrderParams): Promise<Order> => {
    const result = await createOrderMutation.mutateAsync(params);
    return result.order;
  };

  return {
    createOrder,
    isCreatingOrder: createOrderMutation.isPending,
    cancelOrder: cancelOrderMutation.mutateAsync,
    isCancellingOrder: cancelOrderMutation.isPending,
    closePosition: closePositionMutation.mutateAsync,
    isClosingPosition: closePositionMutation.isPending,
  };
}
